import React, { useEffect, useRef, useState } from 'react';
import Reveal from 'reveal.js';
import 'reveal.js/dist/reveal.css';
import SlideTitle from './slides/SlideTitle';
import SlideMetrics from './slides/SlideMetrics';
import SlideChart from './slides/SlideChart';
import SlideInsights from './slides/SlideInsights';
import SlideSummary from './slides/SlideSummary';
import SlideRating from './slides/SlideRating';
import ExportButton from './controls/ExportButton';
import ShareButton from './controls/ShareButton';
import './styles/reveal-theme.css';

/**
 * ReportViewer Component
 * Main container for Interactive BankReport presentation
 * 
 * Features:
 * - Reveal.js integration
 * - Keyboard navigation
 * - Touch gestures
 * - Progress bar
 * - Fullscreen mode
 * - Export controls
 */
const ReportViewer = ({ reportData, reportId }) => {
  const deckRef = useRef(null);
  const revealRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Initialize Reveal.js
  useEffect(() => {
    if (!deckRef.current || revealRef.current) return;

    const deck = new Reveal(deckRef.current, {
      // Display
      width: 1920,
      height: 1080,
      margin: 0,
      minScale: 0.2,
      maxScale: 2.0,

      // Controls
      controls: true,
      controlsLayout: 'bottom-right',
      controlsBackArrows: 'faded',
      progress: true,
      slideNumber: 'c/t',
      showSlideNumber: 'all',
      hash: true,
      respondToHashChanges: true,

      // Navigation
      keyboard: true,
      overview: true,
      center: false,
      touch: true,
      loop: false,
      rtl: false,
      navigationMode: 'default',
      shuffle: false,
      fragments: true,
      fragmentInURL: true,
      embedded: false,
      help: true,
      pause: true,
      showNotes: false,

      // Presentation Mode
      autoSlide: 0,
      autoSlideStoppable: true,
      autoSlideMethod: null,
      defaultTiming: null,
      mouseWheel: false,
      previewLinks: false,
      postMessage: true,
      postMessageEvents: false,
      focusBodyOnPageVisibilityChange: true,

      // Transitions
      transition: 'slide', // none/fade/slide/convex/concave/zoom
      transitionSpeed: 'default', // default/fast/slow
      backgroundTransition: 'fade',

      // Parallax
      parallaxBackgroundImage: '',
      parallaxBackgroundSize: '',
      parallaxBackgroundHorizontal: null,
      parallaxBackgroundVertical: null,

      // Display
      display: 'block',
      hideInactiveCursor: true,
      hideCursorTime: 5000,

      // Plugins
      plugins: []
    });

    deck.initialize().then(() => {
      console.log('[REVEAL] Initialized successfully');
      revealRef.current = deck;
      setIsReady(true);

      // Listen to slide change events
      deck.on('slidechanged', (event) => {
        setCurrentSlide(event.indexh);
        console.log('[REVEAL] Slide changed to:', event.indexh);
      });
    });

    return () => {
      if (revealRef.current) {
        revealRef.current.destroy();
        revealRef.current = null;
      }
    };
  }, []);

  // Render slide based on type
  const renderSlide = (slide, index) => {
    switch (slide.type) {
      case 'title':
        return (
          <SlideTitle
            key={index}
            title={slide.title}
            subtitle={slide.subtitle}
            propertyName={slide.propertyName}
            location={slide.location}
            date={slide.date}
          />
        );

      case 'metrics':
        return (
          <SlideMetrics
            key={index}
            title={slide.title}
            metrics={slide.metrics}
          />
        );

      case 'chart':
        return (
          <SlideChart
            key={index}
            title={slide.title}
            subtitle={slide.subtitle}
            chartType={slide.chartType}
            chartConfig={slide.chartConfig}
          />
        );

      case 'insight':
        return (
          <SlideInsights
            key={index}
            title={slide.title}
            insights={slide.insights}
          />
        );

      case 'summary':
        return (
          <SlideSummary
            key={index}
            title={slide.title}
            takeaways={slide.takeaways}
            cta={slide.cta}
            contact={slide.contact}
          />
        );

      case 'rating':
        return (
          <SlideRating
            key={index}
            rating={slide.rating}
            ratingLabel={slide.ratingLabel}
            summary={slide.summary}
            recommendations={slide.recommendations}
            criteria={slide.criteria}
          />
        );

      default:
        return (
          <div key={index} className="flex items-center justify-center h-full">
            <p className="text-gray-500">Unknown slide type: {slide.type}</p>
          </div>
        );
    }
  };

  if (!reportData || !reportData.slides) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Präsentation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen bg-black">
      {/* Reveal.js Container */}
      <div ref={deckRef} className="reveal">
        <div className="slides">
          {reportData.slides.map((slide, index) => (
            <section key={index}>
              {renderSlide(slide, index)}
            </section>
          ))}
        </div>
      </div>

      {/* Control Bar */}
      {isReady && (
        <div className="fixed top-4 right-4 z-50 flex items-center space-x-2">
          <ExportButton 
            reportData={reportData} 
            reportId={reportId}
            deckRef={revealRef}
          />
          <ShareButton 
            reportId={reportId}
          />
          
          {/* Fullscreen Toggle */}
          <button
            onClick={() => {
              if (revealRef.current) {
                const elem = deckRef.current;
                if (!document.fullscreenElement) {
                  elem.requestFullscreen();
                } else {
                  document.exitFullscreen();
                }
              }
            }}
            className="px-4 py-2 bg-white/90 hover:bg-white text-gray-800 rounded-lg shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-105"
            title="Vollbild"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>

          {/* Help Button */}
          <button
            onClick={() => {
              if (revealRef.current) {
                revealRef.current.toggleHelp();
              }
            }}
            className="px-4 py-2 bg-white/90 hover:bg-white text-gray-800 rounded-lg shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-105"
            title="Hilfe"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Initialisiere Präsentation...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportViewer;

